import { Close as CloseIcon } from '@mui/icons-material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CleaningServicesOutlinedIcon from '@mui/icons-material/CleaningServicesOutlined';
import KitchenOutlinedIcon from '@mui/icons-material/KitchenOutlined';
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined';
import {
  Alert,
  alpha,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  Fade,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import type { QrTargetType } from '@prospero/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { diffDaysFromAppToday } from '../config/timezone';
import { useResolveQrPreviewQuery } from '../hooks/qr-binding/use-qr-binding-queries';
import { useDebouncedValue } from '../hooks/use-debounced-value';
import { useInspectionQrIconName } from '../hooks/use-inspection-qr-icon-name';
import { useIsMobile } from '../hooks/use-is-mobile';
import { useQrIconName } from '../hooks/use-qr-icon-name';
import { useQrScanner } from '../hooks/use-qr-scanner';
import { getLotStateColors, resolveLotVisualState } from '../pages/lots/utils/lot-visual-state';
import { formatDate } from '../utils/date-formatter';
import { formatDateKey } from './calendar/calendar-utils';
import { LotExpiryChip } from './lots/lot-expiry-chip';
import { QrIconOverlay } from './qr-icon-overlay';
import { SlideUpTransition } from './transitions/slide-up-transition';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[47][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// Matches the /public/ddt/<uuid> path regardless of origin/scheme (or a bare
// path, if a QR ever encodes one). Anchored at the end so a trailing extra
// segment (e.g. /public/ddt/<uuid>/foo) is rejected, not just "starts with".
const DDT_PATH_REGEX =
  /\/public\/ddt\/([0-9a-f]{8}-[0-9a-f]{4}-[47][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})\/?$/i;

function extractDdtId(scanned: string): string | null {
  return scanned.match(DDT_PATH_REGEX)?.[1] ?? null;
}

const PREVIEW_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

const previewAnimations = {
  '@keyframes previewCardEnter': {
    '0%': { opacity: 0, transform: 'translateY(14px) scale(0.985)' },
    '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
  },
  '@keyframes previewLineReveal': {
    '0%': { opacity: 0, transform: 'translateY(8px)' },
    '100%': { opacity: 1, transform: 'translateY(0)' },
  },
  '@keyframes previewButtonShine': {
    '0%': { transform: 'translateX(-120%) skewX(-12deg)' },
    '60%': { transform: 'translateX(180%) skewX(-12deg)' },
    '100%': { transform: 'translateX(180%) skewX(-12deg)' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    '@keyframes previewCardEnter': {
      '0%': { opacity: 0, transform: 'none' },
      '100%': { opacity: 1, transform: 'none' },
    },
    '@keyframes previewLineReveal': {
      '0%': { opacity: 0 },
      '100%': { opacity: 1 },
    },
    '@keyframes previewButtonShine': {
      '0%, 100%': { transform: 'translateX(-120%)' },
    },
  },
} as const;

function resolveViewfinderBorderColor(decoded: boolean, showConfirmation: boolean): string {
  if (decoded) {
    return 'success.main';
  }
  if (showConfirmation) {
    return 'success.light';
  }
  return 'white';
}

function resolveScannerError(
  scannerError: string | null,
  t: (key: string) => string,
): string | null {
  if (!scannerError) {
    return null;
  }
  if (scannerError === 'NO_MEDIA_DEVICES') {
    return t('cameraPermissionDenied');
  }
  return t('qrScanError');
}

function getKindIcon(kind: QrTargetType) {
  switch (kind) {
    case 'TEMPERATURE_EQUIPMENT':
      return <KitchenOutlinedIcon fontSize="small" />;
    case 'OIL_FRYER':
      return <LocalFireDepartmentOutlinedIcon fontSize="small" />;
    case 'CLEANING_AREA':
      return <CleaningServicesOutlinedIcon fontSize="small" />;
    case 'PEST_CONTROL_POINT':
      return <BugReportOutlinedIcon fontSize="small" />;
    case 'METAL_DETECTOR':
      return <SensorsOutlinedIcon fontSize="small" />;
    default:
      return null;
  }
}

function getKindLabelKey(kind: QrTargetType): string {
  switch (kind) {
    case 'TEMPERATURE_EQUIPMENT':
      return 'preview.kind.temperatureEquipment';
    case 'OIL_FRYER':
      return 'preview.kind.oilFryer';
    case 'CLEANING_AREA':
      return 'preview.kind.cleaningArea';
    case 'PEST_CONTROL_POINT':
      return 'preview.kind.pestControlPoint';
    case 'METAL_DETECTOR':
      return 'preview.kind.metalDetector';
    default:
      return 'preview.kind.temperatureEquipment';
  }
}

function getHttpErrorStatus(err: unknown): number | null {
  if (
    err !== null &&
    typeof err === 'object' &&
    'response' in err &&
    err.response !== null &&
    typeof err.response === 'object' &&
    'status' in err.response &&
    typeof err.response.status === 'number'
  ) {
    return err.response.status;
  }
  return null;
}

interface ConfirmButtonProps {
  isSubmitting: boolean;
  disabled?: boolean;
  onConfirm: () => void;
  label: string;
  accent?: { main: string; dark: string };
  delayMs?: number;
}

function ConfirmButton({
  isSubmitting,
  disabled = false,
  onConfirm,
  label,
  accent,
  delayMs = 0,
}: ConfirmButtonProps) {
  const theme = useTheme();
  const mainColor = accent?.main ?? theme.palette.success.main;
  const darkColor = accent?.dark ?? theme.palette.success.dark;
  const isInteractive = !(isSubmitting || disabled);

  return (
    <Button
      onClick={onConfirm}
      disabled={isSubmitting || disabled}
      disableElevation
      endIcon={
        isSubmitting ? null : (
          <ArrowForwardRoundedIcon
            sx={{
              fontSize: 18,
              transition: `transform 240ms ${PREVIEW_EASE}`,
              '.MuiButton-root:hover &': { transform: 'translateX(3px)' },
            }}
          />
        )
      }
      sx={{
        position: 'relative',
        minHeight: 52,
        px: 3,
        borderRadius: 2,
        textTransform: 'none',
        fontWeight: 700,
        fontSize: '0.95rem',
        letterSpacing: 0.1,
        color: '#fff',
        background: `linear-gradient(135deg, ${mainColor} 0%, ${darkColor} 100%)`,
        boxShadow: `0 8px 18px -8px ${alpha(darkColor, 0.55)}, inset 0 1px 0 ${alpha('#fff', 0.18)}`,
        overflow: 'hidden',
        transition: `transform 200ms ${PREVIEW_EASE}, box-shadow 200ms ${PREVIEW_EASE}`,
        opacity: 0,
        animation: `previewLineReveal 380ms ${PREVIEW_EASE} ${delayMs}ms forwards`,
        '&::after': isInteractive
          ? {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              width: '40%',
              background: `linear-gradient(90deg, ${alpha('#fff', 0)} 0%, ${alpha('#fff', 0.32)} 50%, ${alpha('#fff', 0)} 100%)`,
              transform: 'translateX(-120%) skewX(-12deg)',
              animation: `previewButtonShine 1100ms ${PREVIEW_EASE} ${delayMs + 320}ms 1`,
              pointerEvents: 'none',
            }
          : undefined,
        '&:hover': {
          background: `linear-gradient(135deg, ${darkColor} 0%, ${mainColor} 100%)`,
          boxShadow: `0 12px 22px -8px ${alpha(darkColor, 0.65)}, inset 0 1px 0 ${alpha('#fff', 0.2)}`,
          transform: 'translateY(-1px)',
        },
        '&:active': {
          transform: 'translateY(0)',
          boxShadow: `0 4px 10px -4px ${alpha(darkColor, 0.55)}`,
        },
        '&.Mui-disabled': {
          background: alpha(theme.palette.action.disabledBackground, 0.6),
          color: theme.palette.action.disabled,
          boxShadow: 'none',
        },
      }}
    >
      {isSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : label}
    </Button>
  );
}

function previewLineSx(delayMs: number) {
  return {
    opacity: 0,
    animation: `previewLineReveal 360ms ${PREVIEW_EASE} ${delayMs}ms forwards`,
  };
}

interface LotPreviewContentProps {
  lotPreview: NonNullable<ReturnType<typeof useResolveQrPreviewQuery>['data']>['lotPreview'];
  isSubmitting: boolean;
  onConfirm: () => void;
  confirmLabel: string;
}

function LotPreviewContent({
  lotPreview,
  isSubmitting,
  onConfirm,
  confirmLabel,
}: LotPreviewContentProps) {
  const { t } = useTranslation('qrScanner');
  const theme = useTheme();

  if (!lotPreview) {
    return null;
  }

  const normalizedExpiry = normalizeLocalDate(lotPreview.expiryDate);

  const visualState = resolveLotVisualState({
    status: lotPreview.status,
    expiryDate: lotPreview.hasNoExpiry ? null : normalizedExpiry,
  });

  const isWarningState =
    visualState === 'expired' ||
    visualState === 'expiring-today' ||
    visualState === 'expiring-soon' ||
    visualState === 'exhausted';

  const isCriticalState = visualState === 'expired' || visualState === 'exhausted';

  const expiryWarning = resolveExpiryWarning(visualState, normalizedExpiry, t);
  const stateColors = getLotStateColors(theme, visualState);
  const lotBrand = getLotStateColors(theme, 'active');

  return (
    <PlainPreviewStack>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 0.75,
          ...previewLineSx(60),
        }}
      >
        <CheckCircleRoundedIcon
          sx={{ fontSize: 14, color: stateColors.main, flexShrink: 0 }}
          aria-hidden="true"
        />
        <Typography
          variant="caption"
          sx={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: '0.68rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: stateColors.dark,
          }}
        >
          {t('preview.lotNumber')} · {lotPreview.lotNumber}
        </Typography>
      </Box>

      <Typography
        variant="h6"
        sx={{
          fontSize: '1.05rem',
          fontWeight: 700,
          lineHeight: 1.25,
          color: 'text.primary',
          ...previewLineSx(120),
        }}
      >
        {lotPreview.name}
      </Typography>

      {lotPreview.recipeName && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 0.75,
            ...previewLineSx(180),
          }}
        >
          <Box
            sx={{
              width: 4,
              height: 4,
              borderRadius: '50%',
              bgcolor: 'text.disabled',
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
            {t('preview.recipe')}: <strong>{lotPreview.recipeName}</strong>
          </Typography>
        </Box>
      )}

      <Box sx={previewLineSx(240)}>
        <LotExpiryChip
          status={lotPreview.status}
          expiryDate={lotPreview.expiryDate}
          hasNoExpiry={lotPreview.hasNoExpiry}
          exhaustedAt={lotPreview.exhaustedAt}
        />
      </Box>

      {isWarningState && expiryWarning && (
        <Alert
          severity={isCriticalState ? 'error' : 'warning'}
          variant="outlined"
          sx={{
            py: 0.5,
            borderRadius: 1.5,
            fontSize: '0.78rem',
            ...previewLineSx(300),
          }}
        >
          {expiryWarning}
        </Alert>
      )}

      <ConfirmButton
        isSubmitting={isSubmitting}
        onConfirm={onConfirm}
        label={confirmLabel}
        accent={lotBrand}
        delayMs={360}
      />
    </PlainPreviewStack>
  );
}

function normalizeLocalDate(value: Date | string | null): string | null {
  if (value === null) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : formatDateKey(value);
  }
  return value;
}

function resolveExpiryWarning(
  visualState: ReturnType<typeof resolveLotVisualState>,
  normalizedExpiry: string | null,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string | null {
  if (visualState === 'expired') {
    return t('preview.expiryWarning.expired', { date: formatDate(normalizedExpiry) });
  }
  if (visualState === 'expiring-today') {
    return t('preview.expiryWarning.today');
  }
  if (visualState === 'expiring-soon') {
    const diffDays = normalizedExpiry ? (diffDaysFromAppToday(normalizedExpiry) ?? 0) : 0;
    return t('preview.expiryWarning.soon', { days: diffDays, count: diffDays });
  }
  if (visualState === 'exhausted') {
    return t('preview.expiryWarning.exhausted');
  }
  return null;
}

interface EquipmentPreviewContentProps {
  kind: QrTargetType;
  displayName: string;
  isSubmitting: boolean;
  onConfirm: () => void;
  confirmLabel: string;
}

function EquipmentPreviewContent({
  kind,
  displayName,
  isSubmitting,
  onConfirm,
  confirmLabel,
}: EquipmentPreviewContentProps) {
  const { t } = useTranslation('qrScanner');
  const theme = useTheme();
  const icon = getKindIcon(kind);
  const kindLabelKey = getKindLabelKey(kind);

  const accent = {
    main: theme.palette.primary.main,
    dark: theme.palette.primary.dark,
  };
  const lotBrand = getLotStateColors(theme, 'active');

  return (
    <Stack
      spacing={1.5}
      sx={{
        ...previewAnimations,
        mx: 2,
        my: 1.5,
        animation: `previewCardEnter 360ms ${PREVIEW_EASE}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1.25,
          ...previewLineSx(80),
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: alpha(accent.main, 0.1),
            color: accent.dark,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.25 }}>
            {displayName}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontSize: '0.74rem', fontWeight: 500 }}
          >
            {t(kindLabelKey)}
          </Typography>
        </Box>
      </Box>
      <ConfirmButton
        isSubmitting={isSubmitting}
        onConfirm={onConfirm}
        label={confirmLabel}
        accent={lotBrand}
        delayMs={180}
      />
    </Stack>
  );
}

interface ScanPreviewProps {
  showPreview: boolean;
  pendingUuid: string | null;
  previewQuery: ReturnType<typeof useResolveQrPreviewQuery>;
  isSubmitting: boolean;
  onConfirm: () => void;
  confirmLabel?: string;
}

function ScanPreview({
  showPreview,
  pendingUuid,
  previewQuery,
  isSubmitting,
  onConfirm,
  confirmLabel,
}: ScanPreviewProps) {
  const { t } = useTranslation('qrScanner');
  const theme = useTheme();
  const lotBrand = getLotStateColors(theme, 'active');
  const resolvedConfirmLabel = confirmLabel ?? t('preview.openTarget');

  if (!showPreview) {
    return (
      <PlainPreviewStack>
        <Box sx={previewLineSx(60)}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '0.72rem',
              textAlign: 'center',
              color: 'text.secondary',
              wordBreak: 'break-all',
              px: 1,
            }}
          >
            {pendingUuid}
          </Typography>
        </Box>
        <ConfirmButton
          isSubmitting={isSubmitting}
          onConfirm={onConfirm}
          label={t('confirmScan')}
          accent={lotBrand}
          delayMs={140}
        />
      </PlainPreviewStack>
    );
  }

  const isAwaitingPreview =
    previewQuery.isLoading || (previewQuery.data === undefined && !previewQuery.isError);

  if (isAwaitingPreview) {
    return (
      <PlainPreviewStack>
        <Skeleton variant="text" width="40%" height={14} sx={previewLineSx(40)} />
        <Skeleton variant="text" width="75%" height={22} sx={previewLineSx(100)} />
        <Skeleton variant="text" width="55%" height={14} sx={previewLineSx(160)} />
        <Skeleton variant="rounded" height={52} sx={{ borderRadius: 2, ...previewLineSx(220) }} />
      </PlainPreviewStack>
    );
  }

  if (previewQuery.isError) {
    return (
      <ScanPreviewError
        errorStatus={getHttpErrorStatus(previewQuery.error)}
        isSubmitting={isSubmitting}
        onConfirm={onConfirm}
        confirmLabel={resolvedConfirmLabel}
      />
    );
  }

  if (previewQuery.data === null) {
    return (
      <PlainPreviewStack>
        <Alert
          severity="info"
          variant="outlined"
          sx={{ py: 0.5, borderRadius: 1.5, fontSize: '0.82rem', ...previewLineSx(60) }}
        >
          {t('preview.notFound')}
        </Alert>
        <ConfirmButton
          isSubmitting={false}
          disabled
          onConfirm={onConfirm}
          label={resolvedConfirmLabel}
          accent={lotBrand}
          delayMs={160}
        />
      </PlainPreviewStack>
    );
  }

  if (previewQuery.data?.lotPreview) {
    return (
      <LotPreviewContent
        lotPreview={previewQuery.data.lotPreview}
        isSubmitting={isSubmitting}
        onConfirm={onConfirm}
        confirmLabel={resolvedConfirmLabel}
      />
    );
  }

  return (
    <EquipmentPreviewContent
      kind={previewQuery.data.kind}
      displayName={previewQuery.data.displayName}
      isSubmitting={isSubmitting}
      onConfirm={onConfirm}
      confirmLabel={resolvedConfirmLabel}
    />
  );
}

interface DdtScanPreviewProps {
  isSubmitting: boolean;
  onConfirm: () => void;
}

/**
 * Confirmation preview for a scanned /public/ddt/<uuid> QR. Deliberately
 * skips the qr-bindings resolve query (useResolveQrPreviewQuery) used by the
 * lot/equipment flow above: a DDT id is not a qr-binding UUID, so it has no
 * matching binding to resolve.
 */
function DdtScanPreview({ isSubmitting, onConfirm }: DdtScanPreviewProps) {
  const { t } = useTranslation('transportDocuments');
  const theme = useTheme();
  const accent = { main: theme.palette.info.main, dark: theme.palette.info.dark };

  return (
    <PlainPreviewStack>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1.25,
          ...previewLineSx(80),
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: alpha(accent.main, 0.1),
            color: accent.dark,
            flexShrink: 0,
          }}
        >
          <ReceiptLongOutlinedIcon fontSize="small" />
        </Box>
        <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.25 }}>
          {t('scanner.chipLabel')}
        </Typography>
      </Box>
      <ConfirmButton
        isSubmitting={isSubmitting}
        onConfirm={onConfirm}
        label={t('scanner.confirmOpen')}
        accent={accent}
        delayMs={140}
      />
    </PlainPreviewStack>
  );
}

function PlainPreviewStack({ children }: { children: React.ReactNode }) {
  return (
    <Stack
      spacing={1.25}
      sx={{
        ...previewAnimations,
        mx: 2,
        my: 1.5,
        animation: `previewCardEnter 320ms ${PREVIEW_EASE}`,
      }}
    >
      {children}
    </Stack>
  );
}

interface ScanPreviewErrorProps {
  errorStatus: number | null;
  isSubmitting: boolean;
  onConfirm: () => void;
  confirmLabel: string;
}

function ScanPreviewError({
  errorStatus,
  isSubmitting,
  onConfirm,
  confirmLabel,
}: ScanPreviewErrorProps) {
  const { t } = useTranslation('qrScanner');
  const theme = useTheme();
  const lotBrand = getLotStateColors(theme, 'active');

  if (errorStatus === 403) {
    return (
      <PlainPreviewStack>
        <Alert
          severity="warning"
          variant="outlined"
          sx={{ py: 0.5, borderRadius: 1.5, fontSize: '0.82rem', ...previewLineSx(60) }}
        >
          {t('preview.moduleDisabled')}
        </Alert>
        <ConfirmButton
          isSubmitting={false}
          disabled
          onConfirm={onConfirm}
          label={confirmLabel}
          accent={lotBrand}
          delayMs={160}
        />
      </PlainPreviewStack>
    );
  }

  if (errorStatus === 404) {
    return (
      <PlainPreviewStack>
        <Alert
          severity="info"
          variant="outlined"
          sx={{ py: 0.5, borderRadius: 1.5, fontSize: '0.82rem', ...previewLineSx(60) }}
        >
          {t('preview.notFound')}
        </Alert>
        <ConfirmButton
          isSubmitting={false}
          disabled
          onConfirm={onConfirm}
          label={confirmLabel}
          accent={lotBrand}
          delayMs={160}
        />
      </PlainPreviewStack>
    );
  }

  return (
    <PlainPreviewStack>
      <Alert
        severity="warning"
        variant="outlined"
        sx={{ py: 0.5, borderRadius: 1.5, fontSize: '0.82rem', ...previewLineSx(60) }}
      >
        {t('preview.networkError')}
      </Alert>
      <ConfirmButton
        isSubmitting={isSubmitting}
        onConfirm={onConfirm}
        label={confirmLabel}
        accent={lotBrand}
        delayMs={160}
      />
    </PlainPreviewStack>
  );
}

interface QrScannerModalProps {
  open: boolean;
  onClose: () => void;
  onDecode: (qrUuid: string) => void;
  title: string;
  isSubmitting?: boolean;
  showPreview?: boolean;
  confirmLabel?: string;
  /**
   * Where to resolve the overlay icon from. `'inspection'` routes through the
   * inspection session token (for the unauthenticated portal); the default uses the
   * Clerk-authenticated client.
   */
  iconSource?: 'default' | 'inspection';
}

export function QrScannerModal({
  open,
  onClose,
  onDecode,
  title,
  isSubmitting = false,
  showPreview = false,
  confirmLabel,
  iconSource = 'default',
}: QrScannerModalProps) {
  const { t } = useTranslation(['qrScanner', 'common']);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [pendingUuid, setPendingUuid] = useState<string | null>(null);
  const [pendingDdtId, setPendingDdtId] = useState<string | null>(null);

  const handleScan = useCallback((scanned: string) => {
    // DDT QR codes encode a full /public/ddt/<uuid> URL, never a bare UUID:
    // check that first so a DDT scan never falls through to the qr-bindings
    // UUID-only resolve flow below.
    const ddtId = extractDdtId(scanned);
    if (ddtId) {
      setPendingDdtId(ddtId);
      return;
    }
    if (UUID_REGEX.test(scanned)) {
      setPendingUuid(scanned);
    }
  }, []);

  const { error: scannerError, decoded } = useQrScanner(videoRef, handleScan, open);

  const handleConfirm = () => {
    if (pendingUuid && UUID_REGEX.test(pendingUuid)) {
      onDecode(pendingUuid);
    }
  };

  const handleConfirmDdt = () => {
    if (pendingDdtId) {
      onClose();
      navigate(`/public/ddt/${pendingDdtId}`);
    }
  };

  useEffect(() => {
    if (!open) {
      setPendingUuid(null);
      setPendingDdtId(null);
    }
  }, [open]);

  const debouncedUuid = useDebouncedValue(pendingUuid, 250);
  const previewQuery = useResolveQrPreviewQuery(showPreview ? debouncedUuid : null);

  const showConfirmation = pendingUuid !== null;
  const showDdtConfirmation = pendingDdtId !== null;
  // Both hooks are always called (rules of hooks); the inactive source is disabled by
  // passing `null` so only one network request fires.
  const isInspection = iconSource === 'inspection';
  const { iconName: defaultIconName } = useQrIconName(isInspection ? null : pendingUuid);
  const { iconName: inspectionIconName } = useInspectionQrIconName(
    isInspection ? pendingUuid : null,
  );
  const iconName = isInspection ? inspectionIconName : defaultIconName;

  const viewfinderBorderColor = resolveViewfinderBorderColor(
    decoded,
    showConfirmation || showDdtConfirmation,
  );
  const scannerErrorMessage = resolveScannerError(scannerError, t);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      aria-label={title}
      slots={{ transition: SlideUpTransition }}
    >
      <Box
        sx={{
          position: 'relative',
          height: isMobile ? '100dvh' : 'auto',
          minHeight: isMobile ? undefined : 480,
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <IconButton onClick={onClose} edge="start" aria-label={t('common:close')}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, textAlign: 'center', mr: 4 }}>
            {title}
          </Typography>
        </Box>

        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '1 / 1',
            bgcolor: 'black',
            overflow: 'hidden',
          }}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0,0,0,0.4)',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '65%',
                aspectRatio: '1 / 1',
                border: '2px solid',
                borderColor: viewfinderBorderColor,
                borderWidth: decoded || showConfirmation || showDdtConfirmation ? 3 : 2,
                borderRadius: 2,
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.4)',
                transition: 'border-color 0.2s ease, border-width 0.2s ease',
              }}
            >
              <QrIconOverlay iconName={iconName} visible={showConfirmation} />
            </Box>
          </Box>
        </Box>

        <Collapse in={!showConfirmation && !showDdtConfirmation} timeout={260} unmountOnExit>
          <Fade in={!showConfirmation && !showDdtConfirmation} timeout={200}>
            <Typography
              variant="body2"
              sx={{ textAlign: 'center', color: 'text.secondary', py: 1.5, px: 2 }}
            >
              {t('scannerInstructions')}
            </Typography>
          </Fade>
        </Collapse>
        <Collapse in={showConfirmation} timeout={340} mountOnEnter unmountOnExit>
          <ScanPreview
            showPreview={showPreview}
            pendingUuid={pendingUuid}
            previewQuery={previewQuery}
            isSubmitting={isSubmitting}
            onConfirm={handleConfirm}
            confirmLabel={confirmLabel}
          />
        </Collapse>
        <Collapse in={showDdtConfirmation} timeout={340} mountOnEnter unmountOnExit>
          <DdtScanPreview isSubmitting={false} onConfirm={handleConfirmDdt} />
        </Collapse>

        {scannerErrorMessage && (
          <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
            {scannerErrorMessage}
          </Alert>
        )}
      </Box>
    </Dialog>
  );
}
